import {
    DeleteOutlined,
    FileExcelOutlined,
    FileTextOutlined,
    EditOutlined,
    PlusOutlined,
    SearchOutlined,
} from '@ant-design/icons'

import { DownOutlined } from '@ant-design/icons'
import { Empty, Form, Input, Select, Table, message } from 'antd'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import DeletePopUp from '../../../../../components/deletePopUp/deletePopUp'
import {
    apiGetTestCasePool,
    deleteTestCase,
    deleteMultipleTestCases,
} from '../../../redux/testRun/testRun.service'

import {
    createFetchSprints,
    useAsyncSelect,
} from '../../../utils/asyncSelectHelpers'

import AsyncTagField from '../../shared/AsyncTagField'

import StatusPill from '../../shared/StatusPill'

import TagChipsCell from '../../shared/TagChipsCell'

import { resolveTestRunProjectId } from '../../shared/TestRunTabs'

import AddTestCaseModal from '../details/testCaseSection/form/AddTestCaseModal'
import {
    createCrossPageSelectionHandlers,
    getSelectionLabel,
    renderIdColumnTitle,
} from '../details/testCaseSection/TestCaseColums'

import styles from '../../../testrun.module.css'

const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100']

const TestCasePoolList = ({
    projectId: projectIdProp,

    refreshKey = 0,

    onCreateReport = () => {},

    onAddTestCase = () => {},

    onImportTestCases = () => {},

    onSelectionChange = () => {},
}) => {
    const [form] = Form.useForm()

    const projectId = resolveTestRunProjectId(projectIdProp)

    const fetchSprintsForProject = useMemo(
        () => createFetchSprints(projectId),

        [projectId]
    )

    const sprintSelect = useAsyncSelect(fetchSprintsForProject)
    const sprintSelectRef = useRef(null)

    const blurSprintSelect = useCallback(() => {
        // Drop focus after choosing/clearing so the blue ring + caret go away
        requestAnimationFrame(() => {
            sprintSelectRef.current?.blur?.()
        })
    }, [])

    const [loading, setLoading] = useState(true)

    const [poolData, setPoolData] = useState([])

    const [total, setTotal] = useState(0)

    const [currentPage, setCurrentPage] = useState(1)

    const [pageSize, setPageSize] = useState(10)

    const [search, setSearch] = useState('')

    const [debouncedSearch, setDebouncedSearch] = useState('')

    const [selectedRowKeys, setSelectedRowKeys] = useState([])

    const [editTestCaseId, setEditTestCaseId] = useState(null)

    const [selectingAll, setSelectingAll] = useState(false)

    const [creatingReport, setCreatingReport] = useState(false)

    const [deletingMultiple, setDeletingMultiple] = useState(false)

    const [deleteTarget, setDeleteTarget] = useState(null)

    const [deleteLoading, setDeleteLoading] = useState(false)

    // Controlled open state for top filter dropdowns.
    // This avoids issues caused by CSS/pointer-events on the custom arrow area.
    const [sprintDropdownOpen, setSprintDropdownOpen] = useState(false)
    const [tagsDropdownOpen, setTagsDropdownOpen] = useState(false)

    const toggleSprintDropdown = useCallback(
        (event) => {
            event?.preventDefault?.()
            event?.stopPropagation?.()
            const next = !sprintDropdownOpen
            setSprintDropdownOpen(next)
            sprintSelect.onDropdownVisibleChange(next)
            if (next) setTagsDropdownOpen(false)
            if (!next) blurSprintSelect()
        },
        [blurSprintSelect, sprintDropdownOpen, sprintSelect]
    )

    const [projectCaseCount, setProjectCaseCount] = useState(0)

    const [sortInfo, setSortInfo] = useState({
        sortField: 'testId',
        sortType: 'asc',
    })

    const watchedSprint = Form.useWatch('sprint', form)

    const watchedTags = Form.useWatch('tags', form)

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400)

        return () => clearTimeout(timer)
    }, [search])

    const buildListPayload = useCallback(
        (skip, limit) => {
            const values = form.getFieldsValue()

            const sprintId = values?.sprint?.value || values?.sprint || ''

            const tagIds =
                values?.tags?.map((t) => t?.value || t).filter(Boolean) || []

            return {
                skip,

                limit,

                filter: {
                    project: projectId,

                    ...(sprintId ? { sprint: sprintId } : {}),

                    ...(tagIds.length ? { tags: tagIds } : {}),
                },

                ...(debouncedSearch
                    ? {
                          search: {
                              title: debouncedSearch,
                              testId: debouncedSearch,
                          },
                      }
                    : {}),

                sortField: sortInfo.sortField,

                sortType: sortInfo.sortType,
            }
        },

        [debouncedSearch, form, projectId, sortInfo]
    )

    const loadProjectCaseCount = useCallback(async () => {
        if (!projectId) {
            setProjectCaseCount(0)
            return
        }

        try {
            const res = await apiGetTestCasePool({
                skip: 0,
                limit: 1,
                filter: { project: projectId },
            })
            const responseData = res?.data ?? {}
            const list = Array.isArray(responseData.data)
                ? responseData.data
                : []
            const count =
                Number(
                    responseData.count ?? responseData.total ?? list.length
                ) || 0
            setProjectCaseCount(count)
        } catch {
            // Keep previous count on transient failures so filters
            // don't incorrectly activate step 1.
        }
    }, [projectId])

    const loadTestCases = useCallback(async () => {
        if (!projectId) {
            setPoolData([])

            setTotal(0)

            setLoading(false)

            return
        }

        setLoading(true)

        try {
            const res = await apiGetTestCasePool(
                buildListPayload((currentPage - 1) * pageSize, pageSize)
            )

            const responseData = res?.data ?? {}

            const list = Array.isArray(responseData.data)
                ? responseData.data
                : []

            const sortedList = [...list].sort((a, b) => {
                const left = String(a?.testId ?? '')
                const right = String(b?.testId ?? '')
                const cmp = left.localeCompare(right, undefined, {
                    numeric: true,
                    sensitivity: 'base',
                })
                return sortInfo.sortType === 'desc' ? -cmp : cmp
            })

            setPoolData(sortedList)

            const nextTotal =
                responseData.count ?? responseData.total ?? list.length

            setTotal(nextTotal)

            // Keep project-level count in sync when no filters are applied
            const values = form.getFieldsValue()
            const sprintId = values?.sprint?.value || values?.sprint || ''
            const tagIds =
                values?.tags?.map((t) => t?.value || t).filter(Boolean) || []
            const hasFilter =
                Boolean(sprintId) ||
                tagIds.length > 0 ||
                Boolean(debouncedSearch)

            if (!hasFilter) {
                setProjectCaseCount(Number(nextTotal) || 0)
            } else if (list.length > 0) {
                setProjectCaseCount((prev) =>
                    Math.max(prev, Number(nextTotal) || list.length)
                )
            }
        } catch {
            setPoolData([])

            setTotal(0)
        } finally {
            setLoading(false)
        }
    }, [
        buildListPayload,
        currentPage,
        pageSize,
        projectId,
        debouncedSearch,
        form,
        sortInfo,
    ])

    const fetchAllMatchingIds = useCallback(async () => {
        if (!projectId || !total) return []

        const res = await apiGetTestCasePool(buildListPayload(0, total))

        const list = Array.isArray(res?.data?.data) ? res.data.data : []

        return list.map((row) => row._id).filter(Boolean)
    }, [buildListPayload, projectId, total])

    useEffect(() => {
        loadTestCases()
    }, [loadTestCases, refreshKey])

    useEffect(() => {
        loadProjectCaseCount()
    }, [loadProjectCaseCount, refreshKey])

    useEffect(() => {
        onSelectionChange(selectedRowKeys)
    }, [selectedRowKeys, onSelectionChange])

    const handleFilterChange = () => {
        setCurrentPage(1)

        setSelectedRowKeys([])

        setTimeout(() => loadTestCases(), 0)
    }

    const { handleRowSelectionChange, handleSelectAll } = useMemo(
        () =>
            createCrossPageSelectionHandlers({
                setSelectedRowKeys,
                setSelectingAll,
                fetchAllMatchingIds,
            }),
        [fetchAllMatchingIds]
    )

    const selectedCount = selectedRowKeys.length
    const hasSelected = selectedCount > 0
    const sprintFilterId = watchedSprint?.value || watchedSprint || ''
    const tagFilterIds =
        (watchedTags || []).map((t) => t?.value || t).filter(Boolean) || []

    const hasActiveFilter =
        Boolean(sprintFilterId) ||
        tagFilterIds.length > 0 ||
        Boolean(debouncedSearch)

    const selectionMeta = useMemo(
        () => ({
            selectedCount,
            totalCount: total,
            total,
            hasActiveFilter,
        }),
        [hasActiveFilter, selectedCount, total]
    )

    const selectionLabel = getSelectionLabel(selectionMeta)

    const columns = useMemo(
        () => [
            {
                title: renderIdColumnTitle(selectionMeta),
                dataIndex: 'testId',
                key: 'testId',
                width: selectionLabel ? 220 : 110,
                sorter: true,
                sortOrder:
                    sortInfo.sortField === 'testId'
                        ? sortInfo.sortType === 'asc'
                            ? 'ascend'
                            : 'descend'
                        : null,
                render: (id) => <span className={styles.poolIdCell}>{id}</span>,
            },
            {
                title: 'Title',
                dataIndex: 'title',
                ellipsis: true,
            },
            {
                title: 'Status',
                dataIndex: 'status',
                width: 100,
                render: (status) => <StatusPill status={status} />,
            },
            {
                title: 'Sprint',
                dataIndex: 'sprint',
                width: 130,
                render: (sprint) => sprint?.title || '—',
            },
            {
                title: 'Tags',
                dataIndex: 'tags',
                width: 200,
                render: (tags) => <TagChipsCell tags={tags} />,
            },
            {
                title: 'Action',
                key: 'action',
                width: 112,
                align: 'center',
                render: (_text, record) => (
                    <div className={styles.poolRowActions}>
                        <button
                            type="button"
                            className={styles.poolEditBtn}
                            title="Edit test case"
                            onClick={() => setEditTestCaseId(record._id)}
                        >
                            <EditOutlined />
                        </button>
                        <button
                            type="button"
                            className={`${styles.poolEditBtn} ${styles.poolDeleteBtn}`}
                            title="Delete test case"
                            onClick={() =>
                                setDeleteTarget({
                                    type: 'single',
                                    id: record._id,
                                })
                            }
                        >
                            <DeleteOutlined />
                        </button>
                    </div>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectionLabel, selectionMeta, sortInfo]
    )

    const rowSelection = useMemo(
        () => ({
            selectedRowKeys,
            onChange: handleRowSelectionChange,
            onSelectAll: handleSelectAll,
            preserveSelectedRowKeys: true,
            getCheckboxProps: () => ({
                disabled: selectingAll,
            }),
        }),
        [
            handleRowSelectionChange,
            handleSelectAll,
            selectingAll,
            selectedRowKeys,
        ]
    )

    const activeFilterContext = {
        sprint: sprintFilterId,

        tags: tagFilterIds,

        search: debouncedSearch,
    }

    const canCreateReport = hasSelected

    const reportCount = selectedCount

    // Step 1 stays active only when the project truly has no cases
    // and no filters are applied. An empty sprint/tag filter must not
    // highlight "Add test case".
    const step1Active = projectCaseCount === 0 && !hasActiveFilter
    const step1Done = projectCaseCount > 0
    const step2Active =
        !step1Active &&
        !hasSelected &&
        !canCreateReport &&
        (projectCaseCount > 0 || hasActiveFilter)
    const step2Done = hasSelected
    const step3Active = canCreateReport

    const handleCreateReportClick = async () => {
        if (!canCreateReport) return

        setCreatingReport(true)

        try {
            const testcaseIds = selectedRowKeys.filter(Boolean)

            if (!testcaseIds.length) {
                message.warning('Please select at least one test case')

                return
            }

            onCreateReport({
                testcaseIds,

                filter: activeFilterContext,
            })
        } catch {
            message.error('Failed to prepare test cases for report')
        } finally {
            setCreatingReport(false)
        }
    }

    const closeDeletePopup = () => {
        if (deleteLoading || deletingMultiple) return
        setDeleteTarget(null)
        setDeleteLoading(false)
    }

    const handleDeleteTestCase = async (id) => {
        if (!id) return false
        setDeleteLoading(true)
        try {
            const resp = await deleteTestCase(id)
            if (resp?.data?.success) {
                message.success(resp?.data?.message || 'Test case deleted')
                setSelectedRowKeys((prev) =>
                    prev.filter((selectedId) => selectedId !== id)
                )
                setDeleteTarget(null)
                loadTestCases()
                loadProjectCaseCount()
                return true
            }
            message.error(resp?.data?.message || 'Failed to delete test case')
            return false
        } catch {
            message.error('Failed to delete test case')
            return false
        } finally {
            setDeleteLoading(false)
        }
    }

    const handleDeleteMultipleTestCases = async () => {
        const ids = selectedRowKeys.filter(Boolean)
        if (!ids.length) return false

        setDeletingMultiple(true)
        setDeleteLoading(true)
        try {
            const resp = await deleteMultipleTestCases(ids)
            if (resp?.data?.success) {
                message.success(
                    resp?.data?.message ||
                        `Deleted ${ids.length} test case${
                            ids.length === 1 ? '' : 's'
                        }`
                )
                setSelectedRowKeys([])
                setDeleteTarget(null)
                loadTestCases()
                loadProjectCaseCount()
                return true
            }
            message.error(
                resp?.data?.message || 'Failed to delete test cases'
            )
            return false
        } catch {
            message.error('Failed to delete test cases')
            return false
        } finally {
            setDeletingMultiple(false)
            setDeleteLoading(false)
        }
    }

    const confirmDeleteTarget = async () => {
        if (!deleteTarget) return
        if (deleteTarget.type === 'single') {
            await handleDeleteTestCase(deleteTarget.id)
            return
        }
        if (deleteTarget.type === 'bulk') {
            await handleDeleteMultipleTestCases()
        }
    }

    return (
        <div className={styles.poolPanel}>
            <div className={styles.poolCard}>
                <div className={styles.flowBannerInCard}>
                    <div className={styles.flowBannerText}>
                        <strong>Test run workflow</strong>

                        <span>
                            Add test cases, select the ones you want to run,
                            then create a test report to record results.
                        </span>
                    </div>

                    <div className={styles.flowSteps}>
                        <span
                            className={
                                step1Active
                                    ? styles.flowStepActive
                                    : step1Done
                                      ? styles.flowStepDone
                                      : styles.flowStep
                            }
                        >
                            1. Add test case
                        </span>

                        <span className={styles.flowStepDivider}>→</span>

                        <span
                            className={
                                step2Active
                                    ? styles.flowStepActive
                                    : step2Done
                                      ? styles.flowStepDone
                                      : styles.flowStep
                            }
                        >
                            2. Select cases
                        </span>

                        <span className={styles.flowStepDivider}>→</span>

                        <span
                            className={
                                step3Active
                                    ? styles.flowStepActive
                                    : styles.flowStep
                            }
                        >
                            3. Create report
                        </span>

                        <span className={styles.flowStepDivider}>→</span>

                        <span className={styles.flowStep}>
                            4. Record results
                        </span>
                    </div>
                </div>

                <div className={styles.poolFilterStrip}>
                    <Input
                        allowClear
                        placeholder="Search by title or id"
                        prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                        className={styles.poolSearch}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value)

                            setCurrentPage(1)

                            setSelectedRowKeys([])
                        }}
                    />

                    <Form
                        form={form}
                        className={styles.poolFilterForm}
                        initialValues={{ tags: [] }}
                        onValuesChange={handleFilterChange}
                    >
                        <Form.Item
                            name="sprint"
                            className={styles.poolFilterField}
                        >
                            <Select
                                ref={sprintSelectRef}
                                allowClear
                                showSearch
                                autoComplete="off"
                                placeholder="All sprints"
                                open={sprintDropdownOpen}
                                filterOption={false}
                                searchValue={sprintSelect.searchValue}
                                autoClearSearchValue={false}
                                suffixIcon={
                                    <span
                                        role="button"
                                        aria-label="Toggle sprint filter"
                                        onMouseDown={toggleSprintDropdown}
                                    >
                                        <DownOutlined
                                            style={{
                                                fontSize: 11,
                                                color: '#64748b',
                                            }}
                                        />
                                    </span>
                                }
                                onSearch={sprintSelect.onSearch}
                                onOpenChange={(open) => {
                                    setSprintDropdownOpen(open)
                                    sprintSelect.onDropdownVisibleChange(open)
                                    if (open) setTagsDropdownOpen(false)
                                    if (!open) blurSprintSelect()
                                }}
                                onClear={sprintSelect.onClear}
                                onPopupScroll={sprintSelect.onPopupScroll}
                                onSelect={() => {
                                    sprintSelect.onSelect()
                                    blurSprintSelect()
                                    setTagsDropdownOpen(false)
                                    setSprintDropdownOpen(false)
                                }}
                                onChange={() => {
                                    sprintSelect.onSelect()
                                    blurSprintSelect()
                                    setTagsDropdownOpen(false)
                                    setSprintDropdownOpen(false)
                                }}
                                options={sprintSelect.options}
                                loading={sprintSelect.loading}
                                notFoundContent={sprintSelect.notFoundContent}
                                optionLabelProp="label"
                            />
                        </Form.Item>

                        <AsyncTagField
                            form={form}
                            projectId={projectId}
                            creatable={false}
                            maxTagCount={1}
                            hideLabel
                            className={`${styles.poolFilterField} ${styles.poolFilterTagsField}`}
                            selectClassName="pool-tag-select"
                            placeholder="All tags"
                            rules={[{ required: false }]}
                            open={tagsDropdownOpen}
                            onOpenChange={(open) => {
                                setTagsDropdownOpen(open)
                                if (open) {
                                    setSprintDropdownOpen(false)
                                    sprintSelect.onDropdownVisibleChange(false)
                                    blurSprintSelect()
                                }
                            }}
                        />
                    </Form>
                    <div className={styles.poolCardActions}>
                        {hasSelected ? (
                            <button
                                type="button"
                                className={`${styles.headerSecondaryButton} ${styles.poolBulkDeleteBtn}`}
                                disabled={deletingMultiple}
                                onClick={() =>
                                    setDeleteTarget({ type: 'bulk' })
                                }
                            >
                                <DeleteOutlined />
                                <span>
                                    {deletingMultiple
                                        ? 'Deleting...'
                                        : `Delete (${selectedCount})`}
                                </span>
                            </button>
                        ) : null}

                        <button
                            type="button"
                            className={styles.headerSecondaryButton}
                            onClick={onImportTestCases}
                        >
                            <FileExcelOutlined />

                            <span>Import Excel</span>
                        </button>

                        <button
                            type="button"
                            className={styles.headerSecondaryButton}
                            onClick={onAddTestCase}
                        >
                            <PlusOutlined />

                            <span>Add Test Case</span>
                        </button>

                        <button
                            type="button"
                            className={`${styles.createButton} ${
                                !canCreateReport || creatingReport
                                    ? styles.createButtonDisabled
                                    : ''
                            }`}
                            disabled={!canCreateReport || creatingReport}
                            onClick={handleCreateReportClick}
                        >
                            <FileTextOutlined />

                            <span>
                                {creatingReport
                                    ? 'Preparing...'
                                    : `Create Report${
                                          canCreateReport
                                              ? ` (${reportCount})`
                                              : ''
                                      }`}
                            </span>
                        </button>
                    </div>
                </div>

                <div className={styles.poolTableWrap}>
                    {!projectId && !loading ? (
                        <Empty description="Open this from a project to load test cases" />
                    ) : (
                        <Table
                            className={styles.reportTable}
                            rowSelection={rowSelection}
                            columns={columns}
                            dataSource={poolData}
                            rowKey="_id"
                            loading={loading || selectingAll}
                            onChange={(
                                _pagination,
                                _filters,
                                sorter,
                                extra
                            ) => {
                                if (extra?.action !== 'sort') return

                                const next = Array.isArray(sorter)
                                    ? sorter[0]
                                    : sorter
                                if (!next?.order) {
                                    setSortInfo({
                                        sortField: 'testId',
                                        sortType: 'asc',
                                    })
                                    setCurrentPage(1)
                                    return
                                }
                                if (
                                    next.field !== 'testId' &&
                                    next.columnKey !== 'testId'
                                ) {
                                    return
                                }
                                setSortInfo({
                                    sortField: 'testId',
                                    sortType:
                                        next.order === 'ascend'
                                            ? 'asc'
                                            : 'desc',
                                })
                                setCurrentPage(1)
                            }}
                            pagination={{
                                current: currentPage,

                                pageSize,

                                total,

                                showSizeChanger: true,

                                pageSizeOptions: PAGE_SIZE_OPTIONS,

                                showTotal: (t, range) =>
                                    `${range[0]}-${range[1]} of ${t}`,

                                onChange: (page, size) => {
                                    setCurrentPage(size !== pageSize ? 1 : page)

                                    setPageSize(size)
                                },
                            }}
                            locale={{
                                emptyText:
                                    'No test cases yet — use Add Test Case to create your first one',
                            }}
                            scroll={{ x: true }}
                        />
                    )}
                </div>
            </div>

            <AddTestCaseModal
                isOpen={Boolean(editTestCaseId)}
                setOpen={(open) => {
                    if (!open) setEditTestCaseId(null)
                }}
                testCaseId={editTestCaseId}
                projectId={projectId}
                onSuccess={loadTestCases}
            />

            <DeletePopUp
                previewDelete={Boolean(deleteTarget)}
                loadingButton={deleteLoading}
                onCancel={closeDeletePopup}
                onDelete={confirmDeleteTarget}
                modalTitle={
                    deleteTarget?.type === 'bulk'
                        ? 'Delete Test Cases'
                        : 'Delete Test Case'
                }
                pageKey={
                    deleteTarget?.type === 'bulk'
                        ? `${selectedCount} selected Test Case${
                              selectedCount === 1 ? '' : 's'
                          }`
                        : 'Test Case'
                }
            />
        </div>
    )
}

export default TestCasePoolList
